import { useMemo, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import { useUIStore } from '@/stores/uiStore';
import { useCapture } from '@/features/capture/useCapture';
import { parseTaskInput } from '@/lib/nlp/nlp';
import { formatScheduledFor, nextDay, startOfLocalDay, withTime } from '@/lib/time';
import { DAY_SHORT } from '@/lib/dateNames';
import { isSameLocalDay } from '@/lib/dateKey';
import { hapticTap } from '@/features/lifecycle/haptics';
import { cn } from '@/lib/cn';
import type { Priority } from '@/types';

export interface CaptureComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PRIORITY_LABEL: Record<Priority, string> = {
  critical: 'crit',
  high: 'high',
  medium: 'med',
  low: 'low',
};

interface Preview {
  chips: string[];
  dest: string;
  /** 'ask' tints the line brand — the question hint. */
  tone: 'task' | 'inbox' | 'ask';
}

/**
 * Presentation-only mirror of useCapture's routing so the user sees the
 * outcome before committing. The commit path stays useCapture — single
 * source of truth. Date-only inputs say "first open slot" instead of running
 * the busy-interval search per keystroke; the exact slot is picked on Return.
 */
function buildPreview(raw: string): Preview | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const parsed = parseTaskInput(trimmed);
  const chips: string[] = [];
  if (parsed.durationMinutes !== null) chips.push(`${parsed.durationMinutes}m`);
  if (parsed.priority !== null) chips.push(PRIORITY_LABEL[parsed.priority]);
  for (const tag of parsed.tags) chips.push(`#${tag}`);

  // Hint only — Return still captures; Ask is never auto-sent (spec §2).
  if (/\?\s*$/.test(trimmed)) {
    return { chips, dest: 'Looks like a question — Ask can answer it', tone: 'ask' };
  }

  const now = new Date();
  const today = startOfLocalDay(now);

  if (parsed.timeMinutes !== null) {
    const base = parsed.date ?? today;
    let candidate = withTime(base, parsed.timeMinutes);
    // Past-today bump: only when the user didn't explicitly set the date.
    if (parsed.date === null && candidate < now) {
      candidate = withTime(nextDay(base), parsed.timeMinutes);
    }
    return { chips, dest: `Creates a task — ${formatScheduledFor(candidate)}`, tone: 'task' };
  }

  if (parsed.date !== null) {
    const d = parsed.date;
    const dayLabel = isSameLocalDay(d, today)
      ? 'today'
      : isSameLocalDay(d, nextDay(today))
        ? 'tomorrow'
        : `${DAY_SHORT[d.getDay()]} ${d.getDate()}`;
    return { chips, dest: `Creates a task — first open slot ${dayLabel}`, tone: 'task' };
  }

  return { chips, dest: 'Goes to Inbox — schedule it anytime', tone: 'inbox' };
}

/**
 * Keyboard-anchored capture composer — the smart bar's default job. Local
 * parse preview, Return = capture (free, instant, offline); "Ask instead"
 * hands the drafted text to the AskSheet. Replaces the centered
 * QuickCaptureDialog on mobile — see
 * docs/specs/2026-07-03-smart-bar-capture-ask.md.
 *
 * Tens-of-times-a-day surface: open rides the keyboard at duration-popover,
 * the preview row is opacity-only with reserved height, nothing springs.
 * No --keyboard-height padding — the WebView shrinks above the keyboard
 * (Keyboard resize: 'native'), which already lifts the focused input (the
 * AskSheet double-compensation lesson).
 */
export function CaptureComposer({ open, onOpenChange }: CaptureComposerProps) {
  const [value, setValue] = useState('');
  const { capture } = useCapture();
  const preview = useMemo(() => buildPreview(value), [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    void hapticTap();
    capture(trimmed);
    setValue('');
    onOpenChange(false);
  };

  const askInstead = () => {
    const trimmed = value.trim();
    void hapticTap();
    const ui = useUIStore.getState();
    ui.setAskSeed(trimmed.length > 0 ? trimmed : null);
    setValue('');
    onOpenChange(false);
    ui.setCommandPaletteOpen(true);
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-overlay bg-scrim ease-out-strong data-[state=open]:duration-popover data-[state=closed]:duration-popover data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 z-overlay rounded-t-2xl border-t border-line-2 bg-canvas px-3 pb-[calc(env(safe-area-inset-bottom)+10px)] pt-3 shadow-window outline-none ease-out-strong data-[state=open]:duration-popover data-[state=closed]:duration-popover data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-4 motion-reduce:animate-none"
        >
          <RadixDialog.Title className="sr-only">Add a task</RadixDialog.Title>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="flex items-center gap-2 rounded-xl border border-line-2 bg-card px-3 py-2.5 focus-within:border-line-3 focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2 focus-within:ring-offset-canvas">
              <Plus className="h-4 w-4 shrink-0 text-t3" aria-hidden />
              <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Add a task…"
                aria-label="Add a task"
                enterKeyHint="done"
                spellCheck={false}
                autoComplete="off"
                data-capture-composer-input
                className="min-w-0 flex-1 bg-transparent text-body-lg text-t1 outline-none placeholder:text-t2"
              />
            </div>

            {/* Reserved height so the sheet never jumps as the parse lands. */}
            <div
              className={cn(
                'mt-2 flex min-h-[22px] flex-wrap items-center gap-x-1.5 gap-y-1 px-1 transition-opacity duration-press',
                preview ? 'opacity-100' : 'opacity-0',
              )}
            >
              {preview?.chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-md bg-rail px-1.5 py-0.5 font-mono text-2xs uppercase tracking-wider text-t2 tabular-nums"
                >
                  {chip}
                </span>
              ))}
              <span
                className={cn(
                  'text-caption',
                  preview?.tone === 'ask' ? 'text-brand-ink' : 'text-t2',
                )}
              >
                {preview?.dest}
              </span>
            </div>

            <div className="mt-2.5 flex gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                leadingIcon={<Sparkles className="h-4 w-4 text-brand" aria-hidden />}
                onClick={askInstead}
              >
                Ask instead
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-[1.3]"
                disabled={!value.trim()}
              >
                Add task
              </Button>
            </div>
          </form>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
